import { ReactElement, ReactNode } from 'react'
import { Box, Typography } from '@mui/material'

export interface PanelHeaderProps {
  title: ReactNode
  description?: ReactNode
  /** Small uppercase eyebrow shown above the title (e.g. "Schritt 1 von 8"). */
  overline?: ReactNode
  /** Optional action rendered at the end (right) of the title row, e.g. an info toggle. */
  action?: ReactNode
}

/**
 * Header for a drawer/panel: an optional overline, a title with an optional end action, and an
 * optional description, with a bottom divider. Reused across the IFC importer drawers and the
 * guided-workflow steps.
 */
export const PanelHeader = ({
  title,
  description,
  overline,
  action,
}: PanelHeaderProps): ReactElement => (
  <Box
    sx={({ palette }) => ({
      p: 2,
      borderBottom: `1px solid ${palette.divider}`,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
    })}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {overline && (
        <Typography variant="overline" color="text.secondary">
          {overline}
        </Typography>
      )}
      <Typography variant="h6">{title}</Typography>
      {description && (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
    {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
  </Box>
)
